import { useContext, useEffect, useRef, useState } from 'react'

import { TargetContext, useTarget, ViewContext } from './context'

import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { JourneyCoreState } from './types'

interface JourneyViewProps {
  name: string
  className?: string
  children: ReactNode
}

/** One screen of the simulated app. Only the active one is visible. */
export const JourneyView = ({ name, className = '', children }: JourneyViewProps) => (
  <div className={`vk-sim-view ${className}`} data-hidden={useContext(ViewContext) !== name}>
    {children}
  </div>
)

export interface JourneyNavItem {
  label: string
  icon: LucideIcon
  /** Target key, when the cursor needs to click this entry. */
  target?: string
  active?: boolean
}

interface JourneyStageProps {
  state: JourneyCoreState
  nav: JourneyNavItem[]
  /** Who the journey is signed in as; some journeys switch person mid-loop. */
  user: { name: string; role?: string }
  children: ReactNode
}

/**
 * The chrome every journey shares: a fixed stage, the mini Veekls sidebar, the
 * virtual cursor and its click ripple. Announcements supply only the screens.
 */
export const JourneyStage = ({ state, nav, user, children }: JourneyStageProps) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const targets = useRef<Record<string, HTMLElement | null>>({})
  const [cursor, setCursor] = useState({ x: 0, y: 0 })

  const registerTarget = useRef((key: string) => (node: HTMLElement | null) => {
    targets.current[key] = node
  }).current

  // Cursor destinations are measured from live geometry rather than hardcoded
  // percentages, so the journey stays accurate at any dialog size.
  useEffect(() => {
    const viewport = viewportRef.current

    if (!viewport) return

    const bounds = viewport.getBoundingClientRect()

    if (!state.target) {
      setCursor({ x: bounds.width / 2, y: bounds.height / 2 })

      return
    }

    const node = targets.current[state.target]

    if (!node) return

    const target = node.getBoundingClientRect()

    setCursor({
      x: target.left - bounds.left + target.width / 2,
      y: target.top - bounds.top + target.height / 2,
    })
  }, [state.target, state.view])

  return (
    <TargetContext.Provider value={registerTarget}>
      <ViewContext.Provider value={state.view}>
        <div ref={viewportRef} className="vk-sim-viewport">
          <div
            className="vk-cursor"
            data-clicking={state.clicking}
            style={{ left: cursor.x, top: cursor.y, opacity: state.cursorVisible ? 1 : 0 }}
          />
          {state.clickSeq > 0 && (
            <span key={state.clickSeq} className="vk-ripple" style={{ left: cursor.x, top: cursor.y }} />
          )}

          {/* Persistent sidebar; only the content pane swaps. */}
          <div className="absolute inset-y-0 left-0 z-10 flex w-[26%] flex-col justify-between border-r border-[#e4e1e4] bg-[#faf8f8] p-2">
            <div className="space-y-1">
              <div className="mb-2 flex items-center gap-1 px-1.5 py-1">
                <img src="/icons/icon-96.png" alt="" className="h-4 w-4" />
                <span className="text-[9px] font-bold tracking-tight text-[#1c1917]">Veekls</span>
              </div>
              {nav.map((item) => (
                <NavItem key={item.label} item={item} />
              ))}
            </div>
            <div className="flex items-center gap-1 border-t border-[#e4e1e4] px-1 pt-1.5">
              <div className="h-3.5 w-3.5 shrink-0 rounded-full bg-stone-300" />
              <div className="vk-persona leading-none" key={user.name}>
                <p className="text-[7px] font-bold text-[#1c1917]">{user.name}</p>
                {user.role && <p className="mt-0.5 text-[7px] text-[#706d71]">{user.role}</p>}
              </div>
            </div>
          </div>

          <div className="absolute inset-y-0 right-0 w-[74%]">{children}</div>
        </div>
      </ViewContext.Provider>
    </TargetContext.Provider>
  )
}

const NavItem = ({ item }: { item: JourneyNavItem }) => {
  const ref = useTarget(item.target ?? '')
  const Icon = item.icon

  return (
    <div
      ref={item.target ? ref : undefined}
      className={`flex items-center gap-1.5 rounded px-1.5 py-1 text-[8px] transition-all duration-200 ${
        item.active ? 'bg-[#e5484d]/10 font-medium text-[#e5484d]' : 'text-[#44403c]'
      }`}
    >
      <Icon className="h-3 w-3" />
      <span>{item.label}</span>
    </div>
  )
}
